import { useEffect, useState, useCallback } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { toast } from "sonner";
import { Coins, Trophy, Loader2, Play, ExternalLink, Anchor, Share2 } from "lucide-react";
import GameCanvas from "./GameCanvas";
import WalletButton from "./WalletButton";
import { gameApi } from "@/lib/gameApi";
import { Link } from "react-router-dom";

const MIN_BALANCE = 1000000; // 1M $JETDOG (hardcoded — stale prod env may still say 2.5M)

const fmt = (n) =>
  Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 4 });

export const Game = () => {
  const { connected, publicKey } = useWallet();
  const wallet = publicKey ? publicKey.toBase58() : null;

  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [pending, setPending] = useState(0);
  const [session, setSession] = useState(null); // {session_id, started_at, signature}
  const [phase, setPhase] = useState("idle"); // idle | starting | playing | ending | gameover
  const [lastRun, setLastRun] = useState(null);
  const [claiming, setClaiming] = useState(false);

  const refreshBalance = useCallback(async () => {
    if (!wallet) return;
    setBalanceLoading(true);
    try {
      let data;
      try {
        data = await gameApi.balance(wallet);
      } catch (first) {
        // one automatic retry — public RPCs can be flaky
        await new Promise((r) => setTimeout(r, 1500));
        data = await gameApi.balance(wallet);
      }
      setBalance(data.balance);
    } catch (e) {
      toast.error("Balance check failed", {
        description: e.response?.data?.detail || e.message,
        action: { label: "Retry", onClick: () => refreshBalance() },
      });
    } finally {
      setBalanceLoading(false);
    }
  }, [wallet]);

  const refreshPending = useCallback(async () => {
    if (!wallet) return;
    try {
      const data = await gameApi.pending(wallet);
      setPending(data.pending);
    } catch (e) {
      // non-fatal
    }
  }, [wallet]);

  useEffect(() => {
    if (connected && wallet) {
      refreshBalance();
      refreshPending();
    } else {
      setBalance(null);
      setPending(0);
      setSession(null);
      setPhase("idle");
    }
  }, [connected, wallet, refreshBalance, refreshPending]);

  const qualifies = balance !== null && balance >= MIN_BALANCE;

  const startGame = async () => {
    if (!wallet) return;
    setPhase("starting");
    try {
      const s = await gameApi.start(wallet);
      setSession(s);
      setBalance(s.balance);
      setPhase("playing");
    } catch (e) {
      toast.error("Cannot start", { description: e.response?.data?.detail || e.message });
      setPhase("idle");
    }
  };

  const handleGameOver = async (coins, durationMs) => {
    if (!session || !wallet) return;
    setPhase("ending");
    try {
      const res = await gameApi.end({
        session_id: session.session_id,
        wallet_address: wallet,
        started_at: session.started_at,
        signature: session.signature,
        coins,
        duration_ms: durationMs,
      });
      setLastRun({ coins, awarded: res.awarded, total: res.pending_total });
      setPending(res.pending_total);
      toast.success(`+${fmt(res.awarded)} $JETDOG earned!`, {
        description: `Pending total: ${fmt(res.pending_total)}`,
      });
      setSession(null);
      setPhase("gameover");
    } catch (e) {
      toast.error("Score submission failed", { description: e.response?.data?.detail || e.message });
      setSession(null);
      setPhase("gameover");
    }
  };

  const claim = async () => {
    if (!wallet || pending <= 0) return;
    setClaiming(true);
    try {
      const res = await gameApi.claim(wallet);
      toast.success(`Claimed ${fmt(res.amount)} $JETDOG!`, {
        description: "Transferred to your wallet. View on Solscan.",
        action: {
          label: "View",
          onClick: () => window.open(res.explorer_url, "_blank"),
        },
        duration: 8000,
      });
      setPending(0);
      // refresh on-chain balance shortly after confirmation
      setTimeout(refreshBalance, 4000);
    } catch (e) {
      toast.error("Claim failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-8 py-10" data-testid="game-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <Link to="/" className="font-display uppercase text-sm underline" data-testid="back-home">
            ← Back to site
          </Link>
          <h1 className="font-display font-bold text-4xl md:text-5xl uppercase leading-none mt-2 neon-title" data-testid="game-title">
            JetDog Surf
          </h1>
          <p className="font-accent text-2xl text-[var(--jd-accent)]" data-testid="game-tagline">
            ride the lanes. collect the bag.
          </p>
        </div>
        <WalletButton testid="game-wallet-button" />
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Game area */}
        <div className="md:col-span-2">
          {!connected ? (
            <ConnectGate />
          ) : balance === null ? (
            <SkeletonGate text={balanceLoading ? "Checking your $JETDOG balance…" : "Loading…"} />
          ) : !qualifies ? (
            <NotEnoughGate balance={balance} />
          ) : phase === "idle" || phase === "starting" ? (
            <StartCard onStart={startGame} loading={phase === "starting"} balance={balance} />
          ) : phase === "playing" ? (
            <GameCanvas onGameOver={handleGameOver} />
          ) : phase === "ending" ? (
            <SkeletonGate text="Submitting your run to the chain…" />
          ) : (
            <GameOverCard
              run={lastRun}
              onAgain={() => {
                setLastRun(null);
                setPhase("idle");
              }}
            />
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-5">
          <StatCard
            icon={Trophy}
            label="On-chain balance"
            value={balance !== null ? `${fmt(balance)} $JETDOG` : "—"}
            sub={`Need ≥ ${fmt(MIN_BALANCE)} to play`}
            ok={qualifies}
            testid="stat-balance"
            action={connected ? { label: "Refresh", onClick: refreshBalance } : null}
          />
          <StatCard
            icon={Coins}
            label="Pending claim"
            value={`${fmt(pending)} $JETDOG`}
            sub="Earned in-game"
            ok={pending > 0}
            testid="stat-pending"
          />
          <button
            type="button"
            onClick={claim}
            disabled={!connected || pending <= 0 || claiming || !qualifies}
            className="nb-btn w-full disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="claim-button"
          >
            {claiming ? <Loader2 size={18} className="animate-spin" /> : <ExternalLink size={18} />}
            {claiming ? "Claiming…" : "Claim to Wallet"}
          </button>
          <RulesCard />
        </aside>
      </div>
    </div>
  );
};

const ConnectGate = () => (
  <div className="gg-panel rounded-3xl p-10 text-center" data-testid="connect-gate">
    <Anchor size={42} className="mx-auto text-[var(--jd-primary)]" />
    <h2 className="font-display font-bold text-3xl uppercase mt-4 neon-title">Connect Wallet to Ride</h2>
    <p className="insert-coin font-mono uppercase tracking-[0.3em] text-[var(--jd-secondary)] mt-3 text-xs">
      ▸ Insert coin — connect wallet ◂
    </p>
    <p className="mt-2 text-[var(--jd-ocean)]">
      You need a Solana wallet with at least{" "}
      <span className="font-display">{fmt(MIN_BALANCE)} $JETDOG</span> to play. Hodl, surf, claim.
    </p>
    <div className="mt-6 inline-flex">
      <WalletButton testid="gate-wallet-button" />
    </div>
  </div>
);

const NotEnoughGate = ({ balance }) => (
  <div className="gg-panel rounded-3xl p-10 text-center" data-testid="not-enough-gate">
    <h2 className="font-display text-3xl uppercase">Not enough $JETDOG</h2>
    <p className="mt-3">
      You currently hold <span className="font-display">{fmt(balance)}</span>. Buy more
      to cross the <span className="font-display">{fmt(MIN_BALANCE)}</span> threshold and ride.
    </p>
    <a
      href="https://pump.fun/8Bdum5DBYXqqi5BeGej45vKVifELQM7pASUfysLTpump"
      target="_blank"
      rel="noreferrer"
      className="nb-btn mt-6 inline-flex"
      data-testid="not-enough-buy"
    >
      Buy on Pump.fun
    </a>
  </div>
);

const SkeletonGate = ({ text }) => (
  <div className="gg-panel rounded-3xl p-10 text-center" data-testid="skeleton-gate">
    <Loader2 className="mx-auto animate-spin" size={32} />
    <p className="mt-3 font-display uppercase">{text}</p>
  </div>
);

const StartCard = ({ onStart, loading, balance }) => (
  <div className="gg-panel rounded-3xl p-10 text-center" data-testid="start-card">
    <h2 className="font-display text-4xl uppercase">Ready to surf?</h2>
    <p className="mt-3">
      Verified as holder: <span className="font-display">{fmt(balance)} $JETDOG</span>.
      Tap below to start a run. Use ←/→ or A/D (swipe on mobile) to change lanes.
    </p>
    <button
      type="button"
      onClick={onStart}
      disabled={loading}
      className="nb-btn mt-6"
      data-testid="start-game-button"
    >
      {loading ? <Loader2 size={18} className="animate-spin" /> : <Play size={18} />}
      {loading ? "Starting…" : "Start Run"}
    </button>
  </div>
);

const GameOverCard = ({ run, onAgain }) => {
  const shareOnX = () => {
    const text = `I just bagged +${fmt(run?.awarded ?? 0)} $JETDOG riding the waves in JetDog Surf! 🌊🐕 Think you can beat my run?`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.origin)}&hashtags=JETDOG,Solana,PlayToEarn`;
    window.open(url, "_blank", "noopener");
  };
  return (
    <div className="gg-panel rounded-3xl p-10 text-center" data-testid="game-over-card">
      <h2 className="font-display text-4xl uppercase">Wipeout!</h2>
      {run && (
        <p className="mt-3">
          Run reward: <span className="font-display">+{fmt(run.awarded)} $JETDOG</span>
          {run.awarded < run.coins && (
            <span className="block text-sm text-[var(--jd-ocean)]">
              (capped from {run.coins} for anti-cheat)
            </span>
          )}
          <span className="block mt-1">
            Pending total: <span className="font-display">{fmt(run.total)}</span>
          </span>
        </p>
      )}
      <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
        <button type="button" onClick={onAgain} className="nb-btn" data-testid="play-again-button">
          <Play size={18} /> Play again
        </button>
        <button type="button" onClick={shareOnX} className="nb-btn nb-btn--white" data-testid="share-x-button">
          <Share2 size={18} /> Share on X
        </button>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, ok, testid, action }) => (
  <div
    className="gg-panel rounded-2xl p-5"
    data-testid={testid}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon size={20} />
        <p className="text-xs uppercase tracking-widest text-[var(--jd-ocean)]">{label}</p>
      </div>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="text-xs underline font-display uppercase"
          data-testid={`${testid}-action`}
        >
          {action.label}
        </button>
      )}
    </div>
    <p
      className="font-display text-2xl mt-2"
      style={{ color: ok ? "var(--jd-text)" : "var(--jd-accent)" }}
    >
      {value}
    </p>
    <p className="text-xs mt-1 text-[var(--jd-ocean)]">{sub}</p>
  </div>
);

const RulesCard = () => (
  <div className="gg-panel gg-glow-cyan rounded-2xl p-5" data-testid="rules-card">
    <p className="font-accent text-lg text-[var(--jd-primary)]">how it works</p>
    <ul className="mt-2 text-sm space-y-1.5 text-[var(--jd-ocean)]">
      <li>→ Connect a Solana wallet (Phantom, Solflare, Trust, WalletConnect)</li>
      <li>→ Hold ≥ 1M $JETDOG to unlock the ride</li>
      <li>→ Dodge buoys & rocks, collect coins</li>
      <li>→ Each in-game coin = 1 $JETDOG</li>
      <li>→ Hit &ldquo;Claim&rdquo; to receive tokens on-chain</li>
    </ul>
  </div>
);

export default Game;
